/* ============================================================================
   MAHA pdp-maha.js
   Bloque de precio del PDP: replica el comportamiento YA implementado y
   probado en las tarjetas Feature Collection (assets/card-product-v2.js) --
   mismo mecanismo de tramos, misma forma de escribir el precio, mismos
   colores/tipografía -- solo cambian los selectores para apuntar al DOM
   del PDP en vez de al de la tarjeta.

   Fuente de los tramos: el MISMO endpoint público + token + cache de
   sessionStorage (mahaVD:{pid}:{vid}) ya integrados en
   assets/card-product-v2.js y assets/cart-mayoreo.js -- no es una llamada
   nueva/adicional al sistema: si el producto ya se consultó desde una
   tarjeta o el carrito en esta sesión, aquí no se repite la petición. No
   existe un objeto window/DOM limpio con los tramos (se comprobó leyendo el
   DOM real): el widget de HulkApps en la PDP (.hulkapps-table) lo inyecta un
   script de terceros fuera del control del tema, así que se reutiliza la
   misma vía ya usada y probada en el resto del proyecto.

   Igual que en la tarjeta: el precio visible (.f-price-item--regular o
   --sale, el que esté activo) se sobreescribe con el precio unitario real
   (base o con descuento) en cada refresh -- nunca se oculta ni se duplica en
   un nodo aparte. La etiqueta "Desde"/"Mayoreo" y el precio tachado son dos
   elementos nuevos, propios del PDP (la tarjeta ya los trae en su Liquid;
   el PDP no, así que se crean una sola vez por JS), pero se comportan
   exactamente igual: [data-price-from]/[data-price-was] con las mismas
   clases de color/tipografía que product-card-v2__price-from/-was.

   No toca el stepper, el formulario de compra ni el motor de descuentos de
   HulkApps: solo lee la cantidad ya escrita y pinta un precio de vista. La
   app de moneda (Transcy) sigue intacta -- de hecho el MutationObserver que
   ya existe en product-when-block.liquid para dividir "$73.95 MXN" en
   .pdp-price-amount/.pdp-price-currency vuelve a correr solo cuando este
   script reescribe el precio, porque observa cambios dentro de
   .product__block--price. El descuento real siempre lo aplica y confirma
   HulkApps en checkout.
   ============================================================================ */
(function () {
  'use strict';
  if (window.__mahaPdpMayoreoInit) return;
  window.__mahaPdpMayoreoInit = true;

  var HULK_URL = 'https://volumediscount.hulkapps.com/v1/products';
  var SHOP_DOMAIN = 'rafael-jjuan.myshopify.com';
  /* Token público ya embebido por la app en el frontend del tema (assets/wishlist.js:985) */
  var HULK_AUTH =
    'eyJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJpc3N1ZXJfbmFtZSIsImF1ZCI6ImNsaWVudCIsInRva2VuX25hbWUiOiJnZXRfcmVzcG9uc2UiLCJzaG9wX2lkIjo2ODI5OCwiY3JlYXRlX3RpbWUiOiIyMDI1LTAxLTI4IDEwOjIyOjAyICswMDAwIn0.3DiZKuoe26tjfnVbYIpF3YTtPo9ibVP_1xKRrVORaZw';
  var CACHE_TTL = 30 * 60 * 1000;
  var MAX_CONCURRENT = 4;

  /* ---------------- money (misma lógica que assets/card-product-v2.js) ---------------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';
    function numberWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount': value = numberWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = numberWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = numberWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = numberWithDelimiters(cents, 0, '.', ','); break;
      default: value = numberWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ---------------- tramos HulkApps (misma lógica que card-product-v2.js / cart-mayoreo.js) ---------------- */
  var inFlight = 0;
  var fetchQueue = [];
  var memCache = {};

  function cacheKey(pid, vid) { return 'mahaVD:' + pid + ':' + vid; }

  function readSession(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || Date.now() - obj.t > CACHE_TTL) return null;
      return obj.tiers;
    } catch (e) { return null; }
  }

  function writeSession(key, tiers) {
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), tiers: tiers })); } catch (e) {}
  }

  function parseOfferLevels(data) {
    var tiers = [];
    try {
      if (!data || !data.eligible_offer || !data.eligible_offer.offer_levels) return tiers;
      if (data.eligible_offer.is_active === false) return tiers;
      var levels = JSON.parse(data.eligible_offer.offer_levels);
      for (var i = 0; i < levels.length; i++) {
        var row = levels[i];
        var type = (row[2] || '').toString();
        if (type.indexOf('%') === -1) continue;
        var min = parseInt(row[0], 10);
        var pct = parseFloat(row[1]);
        if (!isFinite(min) || !isFinite(pct) || min <= 0 || pct <= 0) continue;
        tiers.push({ min: min, percent: pct });
      }
      tiers.sort(function (a, b) { return a.min - b.min; });
    } catch (e) { /* respuesta inesperada -> sin tramos */ }
    return tiers;
  }

  function pump() {
    while (inFlight < MAX_CONCURRENT && fetchQueue.length) {
      inFlight++;
      fetchQueue.shift()();
    }
  }

  function fetchTiers(pid, vid) {
    var key = cacheKey(pid, vid);
    if (memCache[key]) return memCache[key];
    var cached = readSession(key);
    if (cached) {
      memCache[key] = Promise.resolve(cached);
      return memCache[key];
    }
    memCache[key] = new Promise(function (resolve) {
      fetchQueue.push(function () {
        var ctrl = 'AbortController' in window ? new AbortController() : null;
        var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 8000) : null;
        fetch(HULK_URL + '?product_id=' + pid + '&shop_domain=' + SHOP_DOMAIN + '&product_variants=' + vid, {
          method: 'GET',
          headers: { Authorization: HULK_AUTH, 'Content-Type': 'application/json' },
          signal: ctrl ? ctrl.signal : undefined,
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            var tiers = parseOfferLevels(data);
            writeSession(key, tiers);
            resolve(tiers);
          })
          .catch(function () { resolve([]); })
          .finally(function () {
            if (timer) clearTimeout(timer);
            inFlight--;
            pump();
          });
      });
      pump();
    });
    return memCache[key];
  }

  function activeTier(tiers, qty) {
    var t = null;
    for (var i = 0; i < tiers.length; i++) { if (qty >= tiers[i].min) t = tiers[i]; }
    return t;
  }

  function tierUnitCents(baseCents, tier) {
    return Math.round(baseCents * (100 - tier.percent)) / 100;
  }

  /* misma función que visiblePriceItem() de card-product-v2.js: el precio
     visible es --regular normalmente, o --sale si hay oferta nativa activa. */
  function visiblePriceItem(priceRoot) {
    if (priceRoot.classList.contains('f-price--on-sale')) {
      return priceRoot.querySelector('.f-price__sale .f-price-item--sale');
    }
    return priceRoot.querySelector('.f-price__regular .f-price-item--regular');
  }

  /* ---------------- bloque de precio del PDP ---------------- */
  function init() {
    var productInfo = document.querySelector('product-info');
    if (!productInfo) return;
    var productId = productInfo.dataset.productId;
    var sectionId = productInfo.dataset.section;
    if (!productId || !sectionId) return;

    var priceBlock = productInfo.querySelector('.product__block--price');
    if (!priceBlock) return;
    var priceRoot = priceBlock.querySelector('.f-price');
    if (!priceRoot) return;

    /* Segundo objetivo, opcional: precio del sticky ATC (snippets/sticky-
       atc-bar.liquid, misma llamada a {% render 'price' %} -> misma
       estructura .f-price). Si no existe (visibility:hide), queda null y
       todo lo demás lo salta con "if (stickyPriceRoot)". */
    var stickyPriceRoot = document.querySelector('.sticky-atc-bar .f-price');

    /* #price-{{ section.id }} (padre real de .f-price, sin clase estática
       propia -- solo tiene un id dinámico) pasa a fila flex para que
       "Desde"/"Mayoreo" + precio + tachado queden en la misma línea, igual
       que .product-card-v2__price-block en la tarjeta. */
    priceRoot.parentElement.classList.add('pdp-price-row');
    if (stickyPriceRoot) stickyPriceRoot.parentElement.classList.add('pdp-price-row');

    /* Precio/variante inicial: data-initial-variant-id/-price en <product-info>
       (sections/main-product.liquid), NO [data-selected-variant] -- ese script
       solo lo renderiza <variant-selects> y Shopify la omite por completo
       cuando product.has_only_default_variant es true (producto sin opciones
       reales), lo que habría dejado sin mayoreo a esos productos. */
    var initialVariantId = parseInt(productInfo.dataset.initialVariantId, 10);
    var initialVariantPrice = parseInt(productInfo.dataset.initialVariantPrice, 10);
    if (!isFinite(initialVariantId) || !isFinite(initialVariantPrice)) return;

    var moneyFormat =
      (window.FoxTheme && window.FoxTheme.settings && window.FoxTheme.settings.moneyFormat) || '${{amount}}';
    var tiers = [];
    var currentVariantId = initialVariantId;
    var currentBaseCents = initialVariantPrice;

    /* "Desde"/"Mayoreo" (antes del precio) y precio tachado (después) --
       mismos data-attributes/roles que product-card-v2.liquid
       ([data-price-from]/[data-price-was]), creados una sola vez porque el
       PDP no los trae en su Liquid (la tarjeta sí). */
    var fromEl = document.createElement('span');
    fromEl.className = 'pdp-price-from';
    fromEl.setAttribute('data-pdp-price-from', '');
    fromEl.hidden = true;
    priceRoot.insertAdjacentElement('beforebegin', fromEl);

    var wasEl = document.createElement('span');
    wasEl.className = 'pdp-price-was';
    wasEl.setAttribute('data-pdp-price-was', '');
    wasEl.hidden = true;
    priceRoot.insertAdjacentElement('afterend', wasEl);

    var stickyFromEl = null;
    var stickyWasEl = null;
    if (stickyPriceRoot) {
      stickyFromEl = stickyPriceRoot.parentElement.querySelector('[data-pdp-price-from]');
      if (!stickyFromEl) {
        stickyFromEl = document.createElement('span');
        stickyFromEl.className = 'pdp-price-from';
        stickyFromEl.setAttribute('data-pdp-price-from', '');
        stickyFromEl.hidden = true;
        stickyPriceRoot.insertAdjacentElement('beforebegin', stickyFromEl);
      }
      stickyWasEl = stickyPriceRoot.parentElement.querySelector('[data-pdp-price-was]');
      if (!stickyWasEl) {
        stickyWasEl = document.createElement('span');
        stickyWasEl.className = 'pdp-price-was';
        stickyWasEl.setAttribute('data-pdp-price-was', '');
        stickyWasEl.hidden = true;
        stickyPriceRoot.insertAdjacentElement('afterend', stickyWasEl);
      }
    }

    function currentQty() {
      var input = productInfo.querySelector('.quantity__input');
      var qty = input ? parseInt(input.value, 10) : NaN;
      return isFinite(qty) && qty > 0 ? qty : 1;
    }

    /* misma lógica que refresh() de card-product-v2.js: el precio visible
       SIEMPRE se reescribe (base o con descuento), nunca se oculta -- solo
       cambian la etiqueta y el tachado. */
    function render() {
      var qty = currentQty();
      /* MAYOREO-DEFAULT-FIX: hasDiscount se declaraba mas abajo (justo antes de
         showLabel). Se sube aqui para poder usarla tambien en el toggle de
         is-tier-price: hasta ahora ese toggle solo miraba los tramos HulkApps,
         asi que con descuento nativo (compare_at_price) el precio activo se
         quedaba negro aunque la etiqueta dijera 'Mayoreo'. */
      var hasDiscount = priceRoot.classList.contains('f-price--on-sale');
      var tier = tiers.length ? activeTier(tiers, qty) : null;
      var unitCents = tier ? tierUnitCents(currentBaseCents, tier) : currentBaseCents;

      var priceItem = visiblePriceItem(priceRoot);
      if (priceItem) {
        /* innerHTML, no textContent: FoxTheme.settings.moneyFormat viene de
           shop.money_format (snippets/js-variables.liquid) y ya trae el
           wrapper real de Transcy (<span class="transcy-money">...</span>),
           igual que en assets/card-product-v2.js/cart-mayoreo.js -- con
           textContent el navegador no parsea esa etiqueta, la muestra tal
           cual como texto en pantalla. */
        priceItem.innerHTML = formatMoney(unitCents, moneyFormat);
        priceItem.classList.toggle('is-tier-price', !!tier || hasDiscount);
      }

      if (stickyPriceRoot) {
        var stickyPriceItem = visiblePriceItem(stickyPriceRoot);
        if (stickyPriceItem) {
          stickyPriceItem.innerHTML = formatMoney(unitCents, moneyFormat);
          stickyPriceItem.classList.toggle('is-tier-price', !!tier || hasDiscount);
        }
      }

      /* "Desde"/"Mayoreo": igual que la tarjeta, se muestra si hay tramos
         configurados (aunque el activo todavía no aplique) o si hay una
         oferta nativa (compare_at_price); "Mayoreo" cuando el tramo SÍ está
         activo, "Desde" en cualquier otro caso con etiqueta visible. */
      var showLabel = tiers.length > 0 || hasDiscount;
      if (showLabel) {
        fromEl.hidden = false;
        fromEl.textContent = (tier || hasDiscount) ? 'Mayoreo' : 'Desde';
      } else {
        fromEl.hidden = true;
      }

      if (tier) {
        wasEl.innerHTML = formatMoney(currentBaseCents, moneyFormat);
        wasEl.hidden = false;
        /* MAYOREO-PRECIO-ROJO-FIX2: color inline con prioridad important.
           El bloque MAYOREO-PRECIO-ROJO de pdp-maha.css (linea 3358) ya gana
           la cascada -- medido rgb(229,62,62) en el navegador -- pero esto lo
           deja fuera del alcance de cualquier hoja futura o de terceros.
           Solo en la rama con tramo activo: si no hay tramo el elemento va
           oculto y el color da igual. */
        wasEl.style.setProperty('color', '#8b8b91', 'important');
      } else {
        wasEl.hidden = true;
      }

      if (stickyFromEl) {
        if (showLabel) {
          stickyFromEl.hidden = false;
          stickyFromEl.textContent = (tier || hasDiscount) ? 'Mayoreo' : 'Desde';
        } else {
          stickyFromEl.hidden = true;
        }
      }
      if (stickyWasEl) {
        if (tier) {
          stickyWasEl.innerHTML = formatMoney(currentBaseCents, moneyFormat);
          stickyWasEl.hidden = false;
          /* MAYOREO-PRECIO-ROJO-FIX2: mismo refuerzo para la barra sticky,
             donde pdp-maha.css:3250 declara color #888888 !important. */
          stickyWasEl.style.setProperty('color', '#8b8b91', 'important');
        } else {
          stickyWasEl.hidden = true;
        }
      }
    }

    function loadTiersFor(vid) {
      fetchTiers(productId, vid).then(function (t) {
        if (vid !== currentVariantId) return; /* la variante pudo cambiar mientras respondía */
        tiers = t;
        render();
      });
    }

    render();
    loadTiersFor(currentVariantId);
    /* === BADGE-VARIANTE 2026-08-14 ===
       Mantiene las badges automaticas de la PDP (Agotado / -N%) sincronizadas
       con la variante activa. El estado inicial NO se pinta aqui: lo emite
       Liquid en sections/main-product.liquid desde
       product.selected_or_first_available_variant, que es la misma variante que
       el picker publica. Por eso no hay parpadeo -- esto solo mantiene.

       NO abre una segunda suscripcion: se invoca desde el mismo callback de
       variantChange que ya usa el precio, unas lineas mas abajo.

       La formula replica la del Liquid EXACTAMENTE, truncado incluido:
       `divided_by` con divisor entero redondea HACIA ABAJO, no al mas cercano.
       Con la variante de 26 cm de efeso-azul (7865 * 100 / 39328 = 19.9985)
       Liquid da 19; un Math.round daria 20 y las dos fuentes divergirian en el
       mismo producto. De ahi Math.floor.

       Se alterna style.display y no el atributo hidden: custom.css declara
       .et1-badges-pdp .et1-badge { display: flex }, que le gana al display:none
       de la hoja del navegador. El estilo inline si le gana.

       El contenedor se oculta completo cuando no queda ninguna badge visible,
       para que sus 8px de margin-bottom no queden huerfanos. */
    var et1Root = document.querySelector('.et1-badges-pdp');
    var et1Agotado = et1Root && et1Root.querySelector('.et1-badge--agotado');
    var et1Oferta = et1Root && et1Root.querySelector('.et1-badge--oferta');

    function et1Mostrar(el, visible) {
      if (!el) return;
      if (visible) el.style.removeProperty('display');
      else el.style.setProperty('display', 'none');
    }

    function et1RenderBadges(v) {
      if (!et1Root) return;
      var agotado = !v.available;
      var pct = 0;
      if (!agotado && typeof v.compare_at_price === 'number' && v.compare_at_price > v.price) {
        pct = Math.floor((v.compare_at_price - v.price) * 100 / v.compare_at_price);
      }
      et1Mostrar(et1Agotado, agotado);
      if (et1Oferta) {
        if (pct > 0) et1Oferta.textContent = '-' + pct + '%';
        et1Mostrar(et1Oferta, pct > 0);
      }
      var visibles = 0;
      for (var i = 0; i < et1Root.children.length; i++) {
        if (et1Root.children[i].style.display !== 'none') visibles++;
      }
      et1Mostrar(et1Root, visibles > 0);
    }



    /* cantidad: reacciona SOLO al stepper de este producto (el bloque
       principal y, si el estilo "standard" de la barra sticky "Agregar"
       llegara a activarse, también el suyo -- ambos comparten
       data-product-id). No reacciona a tarjetas de otros productos en la
       misma página ("no el de las tarjetas"). */
    document.addEventListener('change', function (e) {
      var input = e.target.closest && e.target.closest('.quantity__input');
      if (!input) return;
      var owner = input.closest('[data-product-id]');
      if (!owner || owner.dataset.productId !== productId) return;
      render();
    });

    /* variante: mismo evento pubsub que ya usa assets/sticky-atc-bar.js para
       mantenerse sincronizado con el selector de variante principal. */
    if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.subscribe) {
      window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, function (event) {
        if (!event || !event.data || event.data.sectionId !== sectionId) return;
        var v = event.data.variant;
        if (!v || !v.id || !isFinite(v.price)) return;
        currentVariantId = v.id;
        currentBaseCents = v.price;
        tiers = [];
        render();
        et1RenderBadges(v);
        loadTiersFor(currentVariantId);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================================
   MAHA — colapso móvil de "Preguntas y respuestas" (app SMPS Q&A,
   #simpAskQuestion). En <=1023.98px muestra solo las primeras 2 preguntas
   (la regla de ocultamiento vive en assets/pdp-maha.css, sección 26.2, vía
   :nth-child) y agrega un botón "Ver más preguntas ↓" / "Ver menos
   preguntas ↑" al final de la lista. En >=1024px el botón no se muestra
   (CSS) y esta clase no tiene ningún efecto (la regla de ocultamiento solo
   existe dentro del media query móvil).
   No toca el HTML/JS de la app: la lista (.accordionSimpQA > ul > li) ya
   viene renderizada server-side por la app (confirmado leyendo el HTML real
   del draft) -- este script solo cuenta los <li> existentes y agrega una
   clase de estado + un botón nuevo, propio, fuera de cualquier elemento de
   la app. El formulario "Hacer una pregunta" (abrir/cerrar/enviar) sigue
   siendo 100% de la app, sin cambios.
   ============================================================================ */
(function () {
  'use strict';
  if (window.__mahaPdpQnaCollapseInit) return;
  window.__mahaPdpQnaCollapseInit = true;

  var VISIBLE_COUNT = 2;

  function init() {
    var list = document.querySelector('#simpAskQuestion .accordionSimpQA');
    if (!list) return;
    var items = list.querySelectorAll('ul > li');
    if (!items.length || items.length <= VISIBLE_COUNT) return;

    list.classList.add('pdp-qna-collapsed');

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'pdp-qna-toggle';
    toggle.textContent = 'Ver más preguntas ↓';
    list.insertAdjacentElement('afterend', toggle);

    var expanded = false;
    toggle.addEventListener('click', function () {
      expanded = !expanded;
      list.classList.toggle('pdp-qna-collapsed', !expanded);
      toggle.textContent = expanded ? 'Ver menos preguntas ↑' : 'Ver más preguntas ↓';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Ancla "Hacer una pregunta sobre el producto" (href="#simpAskQuestion",
     snippets/product-when-block.liquid, bloque text_eKT8hY): el scroll
     nativo del navegador para un ancla deja el bloque pegado ARRIBA del
     viewport. Se intercepta el clic y se reemplaza por scrollIntoView con
     block:'center' para que quede centrado verticalmente. No cambia el
     href/funcionalidad del enlace -- solo el comportamiento del scroll.
     Va fuera de init() a propósito: debe funcionar aunque el producto
     tenga <=2 preguntas (init() corta ahí y nunca llega a esta parte). */
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href="#simpAskQuestion"]');
    if (!link) return;
    var target = document.getElementById('simpAskQuestion');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();

/* ============================================================================
   MAHA descripcion-ver-mas — 2026-08-04
   sections/pdp-descripcion.liquid + assets/pdp-descripcion.js YA implementan
   el toggle "Ver descripción completa / Ver menos". Su único chequeo de "el
   texto cabe sin recortar" corre una vez en DOMContentLoaded comparando
   scrollHeight vs clientHeight -- pero en móvil/tablet el panel de
   Descripción arranca oculto (display:none) porque pdp-mobile-tabs.js deja
   "Ficha Técnica" como pestaña activa por defecto, no "Descripción". Un
   elemento oculto siempre mide 0/0, así que ese chequeo concluye "cabe
   entero" y quita el colapso + esconde el botón para CUALQUIER largo de
   texto. Confirmado en vivo (Playwright, producto con descripción de 4700+
   caracteres): hoy en móvil/tablet la descripción se muestra completa de
   golpe, sin truncar y sin botón -- justo el bug que pide arreglar esta
   tarea. En escritorio pasa lo contrario (sí trunca cuando no debería); ese
   lado se arregla solo con CSS, ver bloque descripcion-ver-mas en
   pdp-maha.css.

   Este bloque re-mide con el mismo criterio (scrollHeight vs clientHeight)
   en el momento real en que el panel se vuelve visible (clic en la pestaña
   "Descripción"), vía MutationObserver sobre su atributo style -- el único
   cambio que pdp-mobile-tabs.js hace para mostrar/ocultar paneles. En
   escritorio (>=1024px) no hace nada: el CSS ya fuerza el texto completo ahí
   sin depender de JS.

   Segundo bug real encontrado en vivo al probar el fix de arriba (Playwright,
   768px): .pdpdesc__body tiene `transition: max-height .35s ease` SIEMPRE
   activa (no solo al hacer toggle manual). Medir clientHeight justo después
   de agregar la clase `collapsed` capta el alto A MITAD DE LA TRANSICIÓN
   (recién iniciada, prácticamente el valor viejo de 1500px), no el alto
   final de 104px -- así que la comparación salía mal en productos cuya
   descripción completa mide menos de 1500px (ej. 768px: 1068px de alto
   real, cabe bajo el tope de 1500px sin recortarse, así que ni siquiera la
   transición a medias lo delataba). Se corrige apagando la transición SOLO
   durante la medición (transition:none + forzar reflow leyendo offsetHeight
   una vez) y restaurándola después -- no afecta la animación real que ve
   el usuario al hacer clic en el botón, que sigue siendo la del CSS.
   Revertir: borrar desde aquí hasta END DESCRIPCION-VER-MAS.
   ============================================================================ */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 1023.98px)';

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function reevaluate(card) {
    if (!window.matchMedia(MOBILE_QUERY).matches) return;
    var body = card.querySelector('[data-desc-body]');
    var btn = card.querySelector('[data-desc-toggle]');
    if (!body || !btn || !isVisible(body)) return;

    var prevTransition = body.style.transition;
    body.style.transition = 'none';
    body.classList.add('collapsed');
    void body.offsetHeight; // fuerza reflow: sin esto se mide a mitad de la transición
    var fits = body.scrollHeight <= body.clientHeight + 4;
    body.style.transition = prevTransition;

    if (fits) {
      body.classList.remove('collapsed');
      btn.style.display = 'none';
    } else {
      btn.style.display = '';
    }
  }

  function initDescVisibilityFix() {
    var cards = document.querySelectorAll('[data-pdp-descripcion]');
    if (!cards.length) return;

    cards.forEach(function (card) {
      var panel = card.closest('[id$="__pdp_descripcion"]') || card.parentElement;
      if (!panel) return;

      // Chequeo inicial por si ya está visible al cargar (escritorio,
      // editor de temas en modo diseño -- ahí pdp-mobile-tabs.js nunca
      // arma pestañas -- o si en el futuro Descripción fuera la pestaña
      // activa por defecto).
      reevaluate(card);

      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === 'style') {
            reevaluate(card);
            break;
          }
        }
      });
      observer.observe(panel, { attributes: true, attributeFilter: ['style'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDescVisibilityFix);
  } else {
    initDescVisibilityFix();
  }
})();
/* END DESCRIPCION-VER-MAS =================================================== */
